// Since you last looked (SCENARIOS K7, PLAN-2026-10-17-compare-and-catching-up
// §4, migration 0068): what happened in a league after a manager last saw its
// front page, as facts a client words for itself.
//
// A LOOK IS THE FRONT PAGE SHOWN, not the league opened: a league opens on My
// Team, and that is managing the team, not catching up. The client says when
// it has shown the catch-up (`PUT`), and the next `GET` counts from there.
//
// FOURTEEN DAYS AT MOST. A look after a month away catches up on the last
// fortnight and says from when (`from`), rather than a month of moves.

import { z } from 'zod';
import { NewsView, TeamRef } from './views';

/** How far back a catch-up reaches, however long ago the last look was. */
export const CATCH_UP_DAYS = 14;
/** How many of the league's moves a catch-up lists; `moveCount` says how many in all. */
export const CATCH_UP_MOVES = 10;
/** How many changes to my players on the injury report it lists; `newsCount` says how many in all. */
export const CATCH_UP_NEWS = 5;
/** The most `moveCount` and `newsCount` count: a catch-up reads News's newest items up to this. */
export const CATCH_UP_COUNTED = 200;

/** One of my games settled since the look. */
export const CatchUpResult = z.object({
  matchupId: z.string(),
  period: z.number().int().min(1),
  /** "Week 5", in the league's own words for the week. */
  label: z.string(),
  opponent: TeamRef,
  myScore: z.number(),
  theirScore: z.number(),
  outcome: z.enum(['won', 'lost', 'tied']),
});
export type CatchUpResult = z.infer<typeof CatchUpResult>;

/** `GET /api/leagues/:id/catch-up`. */
export const CatchUpView = z.object({
  leagueId: z.string(),
  /** The last look, or null for none: a first look catches up on nothing. */
  since: z.string().nullable(),
  /**
   * What the catch-up counts from: `since`, or `CATCH_UP_DAYS` ago when the
   * last look was longer ago than that. Null with no look.
   */
  from: z.string().nullable(),
  /**
   * My games in the weeks settled since the look, oldest first: a week is new
   * when it settled after the last week the look saw settled, however often
   * its scoring has been run again since.
   */
  results: z.array(CatchUpResult),
  /** How many of the league's other games those weeks hold. */
  otherGames: z.number().int().min(0),
  /**
   * The league's adds, claims, drops and trades since `from`, newest first,
   * as News has them: one item for each player a move took on or let go.
   */
  moves: z.array(NewsView).max(CATCH_UP_MOVES),
  /** How many such items, counted up to `CATCH_UP_COUNTED`. */
  moveCount: z.number().int().min(0),
  /** Changes on the injury report to players on my roster since `from`, newest first. */
  news: z.array(NewsView).max(CATCH_UP_NEWS),
  /** How many, counted among News's newest `CATCH_UP_COUNTED` of mine. */
  newsCount: z.number().int().min(0),
});
export type CatchUpView = z.infer<typeof CatchUpView>;

/** `PUT /api/leagues/:id/catch-up`: the look, as it stands after this call. */
export const CatchUpLooked = z.object({
  leagueId: z.string(),
  lookedAt: z.string(),
});
export type CatchUpLooked = z.infer<typeof CatchUpLooked>;
